import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import ProviderCard from "../components/ProviderCard";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Search, SlidersHorizontal, ShowerHead } from "lucide-react";

export default function Directory() {
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState({ categories: [], neighborhoods: [] });
  const [providers, setProviders] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const categoria = params.get("categoria") || "";
  const bairro = params.get("bairro") || "";

  const fetchProviders = useCallback(() => {
    setLoading(true);
    const query = new URLSearchParams();
    if (categoria) query.set("category", categoria);
    if (bairro) query.set("neighborhood", bairro);
    if (q) query.set("q", q);
    api.get(`/providers?${query.toString()}`)
      .then((r) => setProviders(r.data))
      .finally(() => setLoading(false));
  }, [categoria, bairro, q]);

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data));
    api.get("/partners").then((r) => setPartners(r.data));
  }, []);

  useEffect(() => { fetchProviders(); }, [fetchProviders]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <h1 className="text-3xl sm:text-4xl font-bold font-heading text-brand-graphite">Prestadores em Joinville</h1>
      <p className="text-muted-foreground mt-2">Encontre o especialista ideal perto de você</p>

      <div className="bg-white rounded-2xl p-4 mt-6 border border-brand-coral/10 flex flex-col md:flex-row gap-3" data-testid="directory-filters">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome ou serviço..."
            className="pl-9 bg-brand-cream/60 border-0" data-testid="directory-search-input" />
        </div>
        <Select value={categoria || "all"} onValueChange={(v) => setParam("categoria", v === "all" ? "" : v)}>
          <SelectTrigger className="md:w-56 bg-brand-cream/60 border-0" data-testid="directory-category-select">
            <SelectValue placeholder="Categoria" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as categorias</SelectItem>
            {meta.categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={bairro || "all"} onValueChange={(v) => setParam("bairro", v === "all" ? "" : v)}>
          <SelectTrigger className="md:w-48 bg-brand-cream/60 border-0" data-testid="directory-neighborhood-select">
            <SelectValue placeholder="Bairro" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os bairros</SelectItem>
            {meta.neighborhoods.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {partners[0] && (
        <a href={partners[0].link} target="_blank" rel="noreferrer" className="block mt-6" data-testid="directory-partner-banner">
          <div className="rounded-2xl overflow-hidden relative h-28 card-hover">
            <img src={partners[0].banner_url} alt={partners[0].name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-brand-graphite/45 flex items-center px-6">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-white/70">Publicidade</p>
                <p className="font-heading font-semibold text-white text-lg">{partners[0].name}</p>
              </div>
            </div>
          </div>
        </a>
      )}

      {categoria === "banho_automatico" && (
        <div className="mt-6 bg-gradient-to-r from-brand-coral to-[#FF8A6B] rounded-2xl p-6 text-white flex items-start gap-4 shadow-lg" data-testid="machine-coming-soon-banner">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center shrink-0">
            <ShowerHead className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="font-heading font-bold text-lg">Em breve: máquinas automáticas PetMachine na sua cidade!</p>
            <p className="text-white/85 text-sm mt-1 leading-relaxed">
              Nossas máquinas automáticas de banho estão em fase de fabricação e serão instaladas em pontos estratégicos de Joinville.
              Estamos trabalhando para posicioná-las no maior número de bairros possível — aguarde, o melhor banho do seu pet está chegando. 🐾
            </p>
            <p className="text-white/70 text-xs mt-2">Quer ser avisado do lançamento? Cadastre-se grátis e ative as notificações.</p>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 mt-8 text-sm text-muted-foreground" data-testid="directory-results-count">
        <SlidersHorizontal className="w-4 h-4" />
        {loading ? "Buscando..." : `${providers.length} resultado(s)`}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
        {providers.map((p) => <ProviderCard key={p.provider_id} provider={p} categories={meta.categories} />)}
      </div>
      {!loading && providers.length === 0 && (
        <div className="text-center py-20 text-muted-foreground" data-testid="directory-empty">
          <p className="font-heading text-lg">Nenhum prestador encontrado com esses filtros.</p>
          <Button variant="ghost" className="text-brand-coral mt-2" onClick={() => { setParams({}); setQ(""); }}>Limpar filtros</Button>
        </div>
      )}
    </div>
  );
}
